import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Loader, ErrorComponent } from 'shared-components';
import { deleteHTTP, get, post } from 'shared-components/src/utils/http';
import Heading from '../../../../components/Heading';
import Title from '../../../../components/Title';
import Alert from '../../../../components/Alert';
import Button from '../../../../components/Button';
import Card from '../../../../components/Card';
import Text from '../../../../components/Text';
import useAlert from '../../../../hooks/useAlert';
// eslint-disable-next-line import/no-named-as-default
import ExternalTransferEtransferPushForm from './components/ExternalTransferEtransferPushForm';
import { TitleContainer, TextContainer } from './styles';
import { HeadingWrapper } from '../../styles';
import CreateExternalEtransferAccountV2Modal from '../../components/CreateExternalEtransferAccountV2Modal';
import TransferModal from '../../components/TransferModal';
import { etransferPushComponentName, updateFeeAmounts } from './utils';
import EditExternalEtransferAccountV2Modal from '../../components/EditExternalEtransferAccountV2Modal';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';

const PTCEtransferPush = ({ configs }) => {
  const intl = useIntl();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useAlert();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [
    showConfirmDeleteAccountModal,
    setShowConfirmDeleteAccountModal,
  ] = useState(false);
  const [externalAccounts, setExternalAccounts] = useState([]);
  const [pushParams, setPushParams] = useState(null);
  const [feeAmounts, setFeeAmounts] = useState({
    create: '',
  });
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [ipAddress, setIpAddress] = useState('');
  const [deviceFingerprint, setDeviceFingerprint] = useState('');

  useEffect(() => {
    const fetchExternalAccounts = async () => {
      try {
        const [externalAccountsData] = await Promise.all([
          get(
            '/api/v2/cardholder_portal/cardholder/external_accounts?type=external_funding_account'
          ),
        ]);

        setExternalAccounts(externalAccountsData.data);
      } catch (e) {
        setError({ type: 'danger', message: e });
      }
      setLoading(false);
    };

    const fetchProgramFeeConfig = async () => {
      try {
        const res = await get(
          '/api/v1/cardholder_portal/cardholder/fee_configs'
        );
        const fees = updateFeeAmounts(res.data);
        setFeeAmounts(fees);
      } catch (e) {
        setError(e);
      }
    };

    fetchProgramFeeConfig();
    fetchExternalAccounts();
  }, []);

  const generateFingerprint = () => {
    const fingerprintData = [
      navigator.userAgent,
      navigator.language,
      `${window.innerWidth}x${window.innerHeight}`,
      navigator.platform,
      window.devicePixelRatio
    ].join('-');
  
    let hash = 0;
  
    for (let i = 0; i < fingerprintData.length; i += 1) {
      hash = Math.imul(hash, 31) + fingerprintData.charCodeAt(i);
    }
  
    return `FP-${Math.abs(hash)}`;
  };
  
  useEffect(() => {
    const fetchIpAddress = async () => {
      try {
        const response = await fetch('https://api64.ipify.org?format=json');
    
        if (!response.ok) {
          throw new Error(`Failed to fetch IP. Status: ${response.status}`);
        }
    
        const data = await response.json();
        setIpAddress(data.ip);
      } catch (e) {
        setError(e);
      }
    };

    fetchIpAddress();

    const storedFingerprint = localStorage.getItem('deviceFingerprint');
    if (storedFingerprint) {
      setDeviceFingerprint(storedFingerprint);
    } else {
      const newFingerprint = generateFingerprint();
      localStorage.setItem('deviceFingerprint', newFingerprint);
      setDeviceFingerprint(newFingerprint);
    }
  }, []);

  const page = 'etransfer-push';

  const editExternalAccount = account => {
    setSelectedAccount(account);
    setShowEditModal(true);
  };

  const deleteExternalAccount = account => {
    setSelectedAccount(account);
    setShowConfirmDeleteAccountModal(true);
  };

  const closeConfirmDeleteModal = e => {
    e.preventDefault();
    setShowConfirmDeleteAccountModal(false);
    setAlert(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await Promise.all([
        deleteHTTP(
          `/api/v2/cardholder_portal/cardholder/external_accounts/${selectedAccount.id}`
        ),
      ]);
      setExternalAccounts(
        externalAccounts.filter(acc => acc.id !== selectedAccount.id)
      );
      setAlert({
        type: 'success',
        message: intl.messages[`${page}-account-deleted-alert`],
        messageFor: 'account-deleted',
        component: etransferPushComponentName,
      });
      setLoading(false);
      setShowConfirmDeleteAccountModal(false);
    } catch (e) {
      setAlert({
        type: 'danger',
        message: e.message,
        messageFor: 'delete-error',
        component: etransferPushComponentName,
      });
      setLoading(false);
      setShowConfirmDeleteAccountModal(false);
    }
  };

  const renderAlerts = component => {
    return (
      alert &&
      component === alert.component && (
        <Alert
          type={alert.type}
          alert={alert.message}
          alertFor={alert.messageFor}
          page={alert.page || page}
        />
      )
    );
  };

  const renderNewContactButton = () => {
    return (
      <Button
        buttonText={intl.messages[`${page}-new-contact-button`]}
        buttonFor='new-contact'
        page={page}
        onClick={e => {
          e.preventDefault();
          setShowCreateModal(true);
        }}
        type='primary'
      />
    );
  };

  const createPushTransfer = async params => {
    setLoading(true);
    try {
      const updatedParams = { ...params, ip_address: ipAddress, device_fingerprint: deviceFingerprint, device_fingerprint_type: 'UNIQUE_DEVICE_IDENTIFIER', authentication_method: 'PASSWORD'};

      await post(
        '/api/v2/cardholder_portal/cardholder/external_transfers',
        updatedParams
      );

      // set success alert
      setAlert({
        type: 'success',
        message: intl.messages[`${page}-transfer-created-alert`],
        messageFor: 'transfer-created',
        component: etransferPushComponentName,
      });
      setLoading(false);
      setShowConfirmModal(false);
    } catch (e) {
      if (e.message === 'Bad Gateway' || e.message.includes('Failed to')) {
        e.message = intl.messages[`${page}-generic-error-alert`];
      }

      setAlert({
        type: 'danger',
        message: e.message,
        messageFor: 'transfer-error',
        component: etransferPushComponentName,
      });
      setLoading(false);
      setShowConfirmModal(false);
    }
  };

  if (error) return <ErrorComponent />;
  if (loading) return <Loader />;

  return (
    <>
      { 
        configs.allow_etransfer_banner && (
          <Alert
            alert={intl.messages['transfer-etransfer-issue-banner-alert']}
            type="warn"
            alertFor="etransfer-issue-banner"
            page={page}
          />
        ) 
      }
      <HeadingWrapper>
        <Heading page={page} heading={intl.messages[`${page}-heading`]} />
      </HeadingWrapper>
      <Card>
        {externalAccounts.length === 0 ? (
          <>
            {renderAlerts(etransferPushComponentName)}
            <TitleContainer>
              <Title
                title={intl.messages[`${page}-form-title`]}
                titleFor="form"
                page={page}
              />
              {renderNewContactButton()}
            </TitleContainer>
            <br />
            <TextContainer>
              <Alert
                type="info"
                alert={intl.messages[`${page}-add-contact-alert`]}
                alertFor="add-contact"
                page={page}
              />
            </TextContainer>
          </>
        ) : (
          <>
            {renderAlerts(etransferPushComponentName)}
            <TitleContainer>
              <Title
                title={intl.messages[`${page}-form-title`]}
                titleFor="form"
                page={page}
              />
              {renderNewContactButton()}
            </TitleContainer>
            <TextContainer>
              <Text
                page={page}
                textFor="form"
                text={intl.messages[`${page}-form-text`]}
              />
            </TextContainer>
            <ExternalTransferEtransferPushForm
              setPushParams={setPushParams}
              setShowConfirmModal={setShowConfirmModal}
              disableSubmit={externalAccounts.length === 0}
              externalAccounts={externalAccounts}
              page={page}
              editExternalAccount={editExternalAccount}
              deleteExternalAccount={deleteExternalAccount}
              setAlert={setAlert}
            />
          </>
        )}
      </Card>
      {showCreateModal && (
        <CreateExternalEtransferAccountV2Modal
          toggleModal={setShowCreateModal}
          setSuccess={setAlert}
          setExternalAccounts={setExternalAccounts}
          page={page}
        />
      )}
      {showConfirmModal && (
        <TransferModal
          toggleModal={setShowConfirmModal}
          confirmCallback={createPushTransfer}
          params={pushParams}
          feeAmount={feeAmounts.create}
          type="create"
          description="Text for the total amount of fee applied for a successful transfer"
          page={page}
        />
      )}
      {showEditModal && (
        <EditExternalEtransferAccountV2Modal
          toggleModal={setShowEditModal}
          setSuccess={setAlert}
          setExternalAccounts={setExternalAccounts}
          page={page}
          id={selectedAccount.id}
        />
      )}
      {showConfirmDeleteAccountModal && (
        <ConfirmDeleteModal
          toggleModal={closeConfirmDeleteModal}
          loading={loading}
          alert={alert}
          deleteItemValue={selectedAccount.id}
          deleteItemName={selectedAccount.name}
          deleteCallback={handleDelete}
          page={page}
        />
      )}
    </>
  );
};

PTCEtransferPush.propTypes = {
  configs: PropTypes.shape({
    allow_etransfer_banner: PropTypes.bool.isRequired,
  }).isRequired,
};

export default PTCEtransferPush;
